import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ViewportBoundingBox } from '@/app/Teleop/teleopConfig';
import { getTraceId } from '@/lib/logger';
import type { ViewportProfile } from '@/lib/videoFraming';
import {
  pushVideoDebugLogEntry,
  readVideoDebugSnapshot,
  subscribeVideoDebugLogEntries,
  type VideoDebugLogEntry,
  type VideoDebugSnapshot,
} from '@/lib/videoDebug';

type VideoDebugOverlayProps = {
  enabled: boolean;
  videoRef: { current: HTMLVideoElement | null };
  viewportWidthPx: number;
  viewportHeightPx: number;
  viewportProfile: ViewportProfile;
  criticalBoundingBox: ViewportBoundingBox;
  expectedStyle: CSSProperties;
  className?: string;
};

const toDisplayValue = (value: string | number | undefined): string => {
  if (value === undefined || value === null) {
    return 'n/a';
  }
  return String(value);
};

const toPercentSummary = (value: number): string => `${value.toFixed(1)}%`;
const VIDEO_LOG_LINES_TO_SHOW = 140;
const VIDEO_DEBUG_EVENT_NAMES = [
  'loadstart',
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'playing',
  'pause',
  'waiting',
  'stalled',
  'suspend',
  'emptied',
  'error',
  'resize',
] as const;

const formatExpectedStyle = (style: CSSProperties): string => {
  const left = toDisplayValue(style.left as string | number | undefined);
  const top = toDisplayValue(style.top as string | number | undefined);
  const width = toDisplayValue(style.width as string | number | undefined);
  const height = toDisplayValue(style.height as string | number | undefined);
  const objectPosition = toDisplayValue(style.objectPosition as string | number | undefined);
  const transform = toDisplayValue(style.transform as string | number | undefined);
  return `l:${left} t:${top} w:${width} h:${height} pos:${objectPosition} tf:${transform}`;
};

const toClock = (ts: string): string => {
  return ts.slice(11, 19);
};

const summarizeSnapshotForLog = (snapshot: VideoDebugSnapshot): string => {
  return `rs:${snapshot.readyState} ns:${snapshot.networkState} paused:${snapshot.paused} ct:${snapshot.currentTime} dim:${snapshot.videoWidth}x${snapshot.videoHeight} err:${snapshot.errorCode ?? 'none'}`;
};

const VideoDebugOverlay: React.FC<VideoDebugOverlayProps> = ({
  enabled,
  videoRef,
  viewportWidthPx,
  viewportHeightPx,
  viewportProfile,
  criticalBoundingBox,
  expectedStyle,
  className,
}) => {
  const [snapshot, setSnapshot] = useState<VideoDebugSnapshot>(() => readVideoDebugSnapshot(videoRef.current));
  const [logEntries, setLogEntries] = useState<VideoDebugLogEntry[]>([]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const sync = () => {
      setSnapshot(readVideoDebugSnapshot(videoRef.current));
    };
    sync();
    const intervalId = window.setInterval(sync, 450);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, videoRef]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return subscribeVideoDebugLogEntries(setLogEntries);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let activeVideo: HTMLVideoElement | null = null;
    let detachActiveListeners = () => {};

    const detach = () => {
      detachActiveListeners();
      detachActiveListeners = () => {};
      activeVideo = null;
    };

    const attach = (video: HTMLVideoElement | null) => {
      detach();
      if (!video) {
        return;
      }
      activeVideo = video;
      const traceId = getTraceId();
      pushVideoDebugLogEntry({
        ts: new Date().toISOString(),
        level: 'info',
        source: 'video',
        event: 'video.listener.attached',
        msg: 'attached video debug listeners',
        traceId,
      });
      const cleanupCallbacks: Array<() => void> = [];
      VIDEO_DEBUG_EVENT_NAMES.forEach((eventName) => {
        const handler = () => {
          const nextSnapshot = readVideoDebugSnapshot(video);
          const level =
            eventName === 'error' ? 'error' : eventName === 'stalled' || eventName === 'waiting' ? 'warn' : 'info';
          pushVideoDebugLogEntry({
            ts: new Date().toISOString(),
            level,
            source: 'video',
            event: `video.${eventName}`,
            msg: `video ${eventName}`,
            traceId,
            detail: summarizeSnapshotForLog(nextSnapshot),
          });
        };
        video.addEventListener(eventName, handler);
        cleanupCallbacks.push(() => {
          video.removeEventListener(eventName, handler);
        });
      });
      detachActiveListeners = () => {
        cleanupCallbacks.forEach((cleanupCallback) => cleanupCallback());
      };
    };

    const syncAttachment = () => {
      const nextVideo = videoRef.current;
      if (nextVideo !== activeVideo) {
        attach(nextVideo);
      }
    };

    syncAttachment();
    const intervalId = window.setInterval(syncAttachment, 250);
    return () => {
      window.clearInterval(intervalId);
      detach();
    };
  }, [enabled, videoRef]);

  if (!enabled) {
    return null;
  }

  const bboxSummary = `${toPercentSummary(criticalBoundingBox.leftPct)} ${toPercentSummary(criticalBoundingBox.topPct)} ${toPercentSummary(criticalBoundingBox.widthPct)} ${toPercentSummary(criticalBoundingBox.heightPct)}`;
  const visibleEntries = logEntries.slice(-VIDEO_LOG_LINES_TO_SHOW).reverse();

  return (
    <div
      className={
        className ??
        'absolute inset-0 z-[90] overflow-y-auto overscroll-contain bg-black/86 p-2 text-[11px] leading-5 text-cyan-100 backdrop-blur-sm'
      }
    >
      <div className="rounded-lg border border-cyan-300/30 bg-black/45 p-2">
        <div>video_debug</div>
        <div>
          vp {Math.round(viewportWidthPx)}x{Math.round(viewportHeightPx)} {viewportProfile}
        </div>
        <div>bbox {bboxSummary}</div>
        <div>
          video exists:{String(snapshot.exists)} connected:{String(snapshot.isConnected)} srcObj:{String(snapshot.hasSrcObject)}
        </div>
        <div>
          rs:{snapshot.readyState} ns:{snapshot.networkState} paused:{String(snapshot.paused)} t:{snapshot.currentTime}
        </div>
        <div>
          dim:{snapshot.videoWidth}x{snapshot.videoHeight} tracks:{snapshot.srcObjectTracks} {snapshot.srcObjectTrackKinds || 'none'}
        </div>
        <div>
          box l:{snapshot.rectLeft} t:{snapshot.rectTop} w:{snapshot.rectWidth} h:{snapshot.rectHeight}
        </div>
        <div>
          inline l:{snapshot.styleLeft} t:{snapshot.styleTop} w:{snapshot.styleWidth} h:{snapshot.styleHeight}
        </div>
        <div>inline fit:{snapshot.styleObjectFit} pos:{snapshot.styleObjectPosition}</div>
        <div>inline tf:{snapshot.styleTransform || 'none'}</div>
        <div>exp {formatExpectedStyle(expectedStyle)}</div>
        <div>
          err:{snapshot.errorCode ?? 'none'} muted:{String(snapshot.muted)} inline:{String(snapshot.playsInline)}
        </div>
        <div>log_count:{logEntries.length}</div>
      </div>
      <div className="mt-2 rounded-lg border border-cyan-300/30 bg-black/45 p-2 text-[10px] leading-4 text-cyan-50/95">
        {visibleEntries.length === 0 ? (
          <div>logs: none yet</div>
        ) : (
          visibleEntries.map((entry, index) => (
            <div key={`${entry.ts}-${entry.event}-${index}`} className="break-all whitespace-pre-wrap border-b border-cyan-200/10 py-1 last:border-b-0">
              {toClock(entry.ts)} {entry.level[0].toUpperCase()} {entry.source} {entry.event}
              {entry.detail ? ` ${entry.detail}` : ''}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VideoDebugOverlay;
